import { useEffect, useState, useContext } from "react";
import Image from "next/image";
import MobileViewFullScreenModal from "@/components/dialog/MobileViewFullScreenModal/MobileViewFullScreenModal";
// components
import { ContainedButton, QuantityButtons } from "@/components/buttons/Buttons";
import ModifierList from "@/components/productDetail/ProductModifier/ModifierList";
import { Loader } from "@/components/loader/Loader";
import { validateProductModifierModal } from "@/validation/shared/validateProductModifierModal";
import { Formik, Form } from "formik";
import PriceWithCurrency from "@/components/priceWithCurrency/PriceWithCurrency";
import { setToastState } from "@/states/toastBarState";
import MerchantContext from "@/contexts/MerchantContext";
import { XIcon } from "@heroicons/react/outline";
import produce from "immer";
import _ from "lodash";
import Chip from "@/components/chip/Chip";
import AspectRatioSquareContainer from "@/components/shared/AspectRatioSquareContainer";

const ProductModifierModal = ({
  productOriginalPrice,
  showModal,
  handleCloseModal,
  initialModifierGroups,
  mode = "create",
  quantity,
  submitIsLoading,
  item,
  outOfStock,
  handleOnSubmit,
  submitButtonTitle = "Add To Cart",
  itemStatus = "loaded",
  clearFormOnClose = false,
}) => {
  const merchantInfoContext = useContext(MerchantContext);
  const [formState, setFormState] = useState({
    modifierGroups: [],
    quantity: 0,
    status: "none",
  });
  const getInputType = (modifierGroup) => {
    let type = "";
    switch (true) {
      case modifierGroup.modifierGroupType === "choice" &&
        modifierGroup.selectionRangeMin === 1 &&
        modifierGroup.selectionRangeMax === 1:
        type = "radio";
        break;
      case modifierGroup.modifierGroupType === "choice" &&
        modifierGroup.selectionRangeMax - modifierGroup.selectionRangeMin > 0:
        type = "checkbox";
        break;
      default:
        type = "text";
        break;
    }
    return type;
  };

  const getModifierGroupsOutput = (modifierGroupsState) => {
    try {
      const outputList = modifierGroupsState.modifierGroups.map(
        (modifierGroup) => {
          const updatedModifierList = produce(modifierGroup, (draft) => {
            const { modifier, inputType, selectionRangeMin, radioChecked } =
              modifierGroup;

            draft["modifier"] = modifier
              .filter((modifier) => {
                let returnedValue = null;
                if (inputType === "text" && selectionRangeMin < 1) {
                  returnedValue = false;
                } else {
                  switch (inputType) {
                    case "radio":
                      returnedValue = modifier.modifierId === radioChecked;
                      break;
                    case "checkbox":
                      returnedValue = modifierGroup.checkboxGroup.includes(
                        modifier.modifierId
                      );
                      break;
                    default:
                  }
                }
                return returnedValue;
              })
              .map((modifier) => _.omit(modifier, ["price", "isSelected"]));
            //remove form state usage fields
            delete draft["checkboxGroup"];
            delete draft["inputType"];
            delete draft["radioChecked"];
            delete draft["isSelected"];
          });
          return updatedModifierList;
        }
      );
      return {
        selectedModifierGroups: outputList,
        quantity: modifierGroupsState.quantity,
      };
    } catch (error) {
      return null;
    }
  };

  const setItemQuantity = (quantity, setFieldValue) => {
    setFieldValue("quantity", quantity);
  };

  const handleClose = () => {
    handleCloseModal();
    if (clearFormOnClose) {
      setFormState({ modifierGroups: [], quantity: 0, status: "none" });
    }
  };

  useEffect(() => {
    const initFormState = () => {
      if (itemStatus === "loaded") {
        if (initialModifierGroups?.length > 0) {
          let formikState = initialModifierGroups.map((modifierGroup) => {
            const inputType = getInputType(modifierGroup);
            let radioChecked = "";
            let checkboxGroup = [];

            if (modifierGroup?.modifier?.[0]?.isSelected !== undefined) {
              modifierGroup.modifier.forEach((modifier) => {
                switch (true) {
                  case modifier.isSelected && inputType === "radio":
                    radioChecked = modifier.modifierId;
                    break;
                  case modifier.isSelected && inputType === "checkbox":
                    checkboxGroup.push(modifier.modifierId);
                    break;
                  default:
                }
              });
            }

            return {
              ...modifierGroup,
              radioChecked: radioChecked,
              checkboxGroup: checkboxGroup,
              inputType: inputType,
            };
          });
          setFormState({
            modifierGroups: formikState,
            quantity: quantity,
            status: "loaded",
          });
        } else {
          setFormState({ modifierGroups: [], quantity: 1, status: "loaded" });
        }
      } else if (itemStatus === "error") {
        handleClose();
        setFormState({
          modifierGroups: [],
          quantity: 0,
          status: "error",
        });
        setToastState({
          show: true,
          severity: "error",
          message: `An error occured when viewing the selected product.`,
        });
      }
    };
    initFormState();
  }, [initialModifierGroups, itemStatus]);

  return (
    <MobileViewFullScreenModal
      className="bg-gray-100 w-[100%] sm:h-[100%] sm:w-[60%] sm:max-w-[40rem] sm:mt-[2rem] sm:mb-[2rem]"
      open={showModal}
      onClose={handleClose}
    >
      {formState?.status === "loaded" && (
        <Formik
          initialTouched={{
            field: true,
          }}
          initialValues={formState}
          validationSchema={validateProductModifierModal}
          validateOnMount={true}
          onSubmit={(val) => {
            handleOnSubmit(getModifierGroupsOutput(val)).then((val) => {
              if (val?.status === true || val?.status === "true") {
                handleClose();
              }
            });
          }}
        >
          {(formikProps) => {
            console.log({ formikProps });
            return (
              <Form>
                <div className="mb-[6rem] sm:mb-0">
                  <div
                    className="px-5 pt-5 pb-5 sm:pb-0 bg-white w-full fixed sm:static top-0 z-[2] cursor-pointer"
                    onClick={handleClose}
                  >
                    <XIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="bg-white p-5 mb-3 mt-10 sm:mt-0">
                    {/* <div
                      className="relative flex justify-center items-center rounded-full bg-white w-[1rem] h-[1rem] p-1 pb-5 cursor-pointer"
                      onClick={handleClose}
                    >
                      <XIcon className="absolute w-6 h-6 text-primary" />
                    </div> */}

                    <div className="flex gap-5 mt-5">
                      <div className="flex w-[80%]">
                        <div className="w-[4rem] mr-3">
                          <AspectRatioSquareContainer>
                            <Image
                              className={outOfStock ? "grayscale" : ""}
                              src={
                                process.env.BUCKET_URL +
                                (item.image || item.itemImage)
                              }
                              title={item.title}
                              alt={item.title}
                              layout="fill"
                              objectFit="cover"
                            />
                          </AspectRatioSquareContainer>
                        </div>
                        <div>
                          <h4 className="m-0 text-lg break-words">
                            {item.title || item.itemTitle}
                          </h4>
                          {outOfStock && <Chip label={"Out Of Stock"} />}
                        </div>
                      </div>

                      <p className="font-bold m-0 mt-1">
                        <PriceWithCurrency value={productOriginalPrice} />
                      </p>
                    </div>
                  </div>
                  <div className="min-h-[5rem]">
                    {formState?.modifierGroups?.length > 0 && (
                      <ModifierList formikProps={formikProps} mode={mode} />
                    )}
                  </div>
                </div>

                <div className="flex bg-white p-5 fixed bottom-0 left-0 right-0 sm:relative">
                  <QuantityButtons
                    handleClickAdd={() =>
                      setItemQuantity(
                        formikProps.values.quantity + 1,
                        formikProps.setFieldValue
                      )
                    }
                    handleClickRemove={() =>
                      setItemQuantity(
                        formikProps.values.quantity - 1,
                        formikProps.setFieldValue
                      )
                    }
                    quantity={formikProps.values.quantity}
                    // setQuantity={setQuantity}
                    // canEditQuantity={canEditQuantity}
                    buttonDecreaseDisabled={
                      submitIsLoading ||
                      item.salesChannelName === "Facebook Live" ||
                      outOfStock ||
                      formikProps.values.quantity <= 1
                    }
                    buttonIncreaseDisabled={
                      submitIsLoading ||
                      item.quantity >= item.maxQuantity ||
                      item.salesChannelName === "Facebook Live" ||
                      outOfStock
                    }
                  />
                  <ContainedButton
                    className={`ml-3 w-full uppercase ${
                      !formikProps.isValid ? "bg-gray-400" : ""
                    }`}
                    loading={submitIsLoading}
                    type="submit"
                    fontSize="text-[13px]"
                    disabled={outOfStock}
                  >
                    {submitButtonTitle}
                  </ContainedButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
      {formState?.status === "none" && (
        <Loader divHeight={"h-[100vh] sm:h-[20rem]"} />
      )}
    </MobileViewFullScreenModal>
  );
};

export default ProductModifierModal;
